'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiStaffStory} from '@/types/strapi';
import {option} from '@/types/utils';
import {useLocale, useTranslations} from 'next-intl';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useEffect, useState} from 'react';
import {dummyImages} from '../helpers/utils';
import {formDummyRows, LoadingItem} from './LoadingItem';
import NoResult from './NoResult';
import Pagination from './Pagination';

export default function StaffStoryList() {
  const locale = useLocale();
  const t = useTranslations('StaffStory');
  const [searchResults, setSearchResults] = useState<StrapiStaffStory[]>([]);
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('p') || '1');
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [regionList, setRegionList] = useState<option[]>([]);
  const [teamList, setTeamList] = useState<option[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  const getSearchResult = async () => {
    setIsLoading(true);
    const postData = await getStrapiData(locale, 'staff-stories', {
      filters: {
        posted_at: {
          $lte: new Date()
        },
        ...(selectedRegion !== '' && {
          regions: {
            $containsi: selectedRegion
          }
        }),
        ...(selectedTeam !== '' && {
          teams: {
            $containsi: selectedTeam
          }
        })
      },
      populate: ['thumbnail', 'image'],
      pagination: {
        page: currentPage,
        pageSize: 12
      },
      sort: ['priority:desc', 'posted_at:desc']
    });

    const totalPages = postData?.meta?.pagination?.pageCount || 1;
    setTotalPages(totalPages);
    setSearchResults(postData?.data || []);
    setIsLoading(false);
  };

  const getFilters = async () => {
    setRegionList(
      (
        await getStrapiData(locale, 'post-tags', {
          filters: {
            slug: {
              $startsWith: 'staff-story-'
            }
          },
          pagination: {
            pageSize: 1000
          },
          sort: ['title:desc']
        })
      ).data?.map((a: any) => {
        return {
          label: a.title,
          value: a.slug
        };
      }) || []
    );
    setTeamList(
      (
        await getStrapiData(locale, 'post-tags', {
          filters: {
            slug: {
              $startsWith: 'team-'
            }
          },
          pagination: {
            pageSize: 1000
          },
          sort: ['title:desc']
        })
      ).data?.map((a: any) => {
        return {
          label: a.title,
          value: a.slug
        };
      }) || []
    );
  };

  useEffect(() => {
    getFilters();
    getSearchResult();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('p', currentPage.toString());
    window.history.pushState({}, '', url.href);
    getSearchResult();
  }, [currentPage, selectedRegion, selectedTeam]);

  return (
    <div className="questrial text-[18px]">
      <div id="main mt-4">
        {
          <div className="flex justify-start items-center mb-4 flex-wrap gap-4">
            <div className="w-[200px]">
              <div className="mt-2 grid grid-cols-1 ">
                <select
                  value={selectedRegion}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-gray-900 border border-gray-300 text-xs"
                  onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value={''}>{t('allRegion')}</option>
                  {regionList.map((region, index) => (
                    <option key={index} value={region.value}>
                      {region.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none col-start-1 row-start-1 mr-2  justify-self-end text-gray-500">
                  <span className="material-symbols outlined text-xs">
                    unfold_more
                  </span>
                </div>
              </div>
            </div>
            <div className="w-[200px]">
              <div className="mt-2 grid grid-cols-1 ">
                <select
                  value={selectedTeam}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-gray-900 border border-gray-300 text-xs"
                  onChange={(e) => {
                    setSelectedTeam(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value={''}>{t('allTeams')}</option>
                  {teamList.map((team, index) => (
                    <option key={index} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none col-start-1 row-start-1 mr-2  justify-self-end text-gray-500">
                  <span className="material-symbols outlined text-xs">
                    unfold_more
                  </span>
                </div>
              </div>
            </div>
          </div>
        }
        {!isLoading && searchResults.length === 0 && <NoResult />}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {isLoading && (
            <LoadingItem>
              <div className="flex flex-col hover:bg-gray-100 border border-gray-100 mt-4">
                <div className="flex flex-col">
                  <div
                    className={cn(
                      ' flex justify-center items-center bg-gray-100 h-60'
                    )}
                  >
                    <span className="material-symbols outlined opacity-30 text-4xl ">
                      image
                    </span>
                  </div>
                  <div className="my-4 mx-2">
                    <div className="text-[#e31836] font-bold ">
                      {formDummyRows(1)}
                    </div>
                    <div className="text-sm my-2 w-3/4">{formDummyRows(1)}</div>
                    <div className="text-sm mb-2">{formDummyRows(1)}</div>
                    <hr />
                    <div className="text-sm flex items-end justify-between gap-2 mt-4 text-gray-600 ">
                      <div className="text-sm mb-2 w-1/2"></div>
                      <div className="text-sm mb-2 w-1/2 flex justify-end">
                        {formDummyRows(1)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </LoadingItem>
          )}
          {!isLoading &&
            searchResults.map((result: StrapiStaffStory, index) => (
              <div key={index} className="flex flex-col ">
                <Link
                  prefetch={false}
                  href={'/our-people-story/' + result.slug}
                  className="fade-in-up"
                >
                  <div className="flex flex-col hover:bg-gray-100 border border-gray-100">
                    <div className="flex flex-col">
                      <div className=" ">
                        <img
                          src={
                            result.image?.url ??
                            dummyImages[index % dummyImages.length].src
                          }
                          alt={result.name}
                          className="float-left mr-4 mb-4 object-cover object-center aspect-4/3"
                        />
                      </div>
                      <div className="mb-4 mx-2">
                        <div className="text-[#e31836] font-bold overflow-ellipsis overflow-hidden">
                          <div className="line-clamp-1">{result.name}</div>
                        </div>
                        <div className="text-sm mb-2 ">
                          <span className="bg-[#e31836] text-white rounded-md py-1 px-2 text-[10px]">
                            {result.brand}
                          </span>
                        </div>
                        <div
                          className={cn(
                            'mb-4 overflow-ellipsis overflow-hidden',
                            locale === 'en' ? 'h-24' : 'h-12'
                          )}
                        >
                          <div
                            className="text-sm line-clamp-4 pr-2"
                            title={result.job_title}
                          >
                            {result.job_title}
                          </div>
                        </div>
                        <hr />
                        <div className="text-sm flex items-end justify-end gap-2 mt-4 text-gray-600">
                          {t('readMore')}
                          <span className="material-symbols outlined text-sm text-gray-600">
                            arrow_forward_ios
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
        </div>
        {!isLoading && searchResults.length > 0 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
